import { fmt, ddmmyyyy, isZero, when } from "@/lib/format";

export const COLS = [
  { k: "op_bal", l: "Op Bal" },
  { k: "deposit", l: "Deposit", h: "bg-emerald-200 text-emerald-950" },
  { k: "withdrawal", l: "Withdrawal", h: "bg-rose-200 text-rose-950" },
  { k: "cashout", l: "Cashout", h: "bg-orange-200 text-orange-950" },
  { k: "bonus", l: "Bonus" },
  { k: "overlimit", l: "Overlimit" },
  { k: "total", l: "Total Balance", h: "bg-yellow-300 text-yellow-950", strong: true },
  { k: "freeze_bal", l: "Freeze Bal", h: "bg-sky-200 text-sky-950" },
  { k: "active_bal", l: "Active Bal" },
  { k: "available_bal", l: "Available Bal", h: "bg-cyan-300 text-cyan-950", strong: true },
  { k: "pnl", l: "Difference" },
];

const TABLE_COLS = COLS.flatMap((c) => (c.k === "cashout" ? [c, { k: "cashout_to", l: "Paid To", h: "bg-orange-100 text-orange-950" }, { k: "cashout_note", l: "Cashout Desc", h: "bg-orange-100 text-orange-950" }] : [c]));

const PnlCell = ({ v }) => (
  <td data-testid="entry-pnl-cell"
    className={`px-3 py-2.5 text-right font-mono tabular-nums font-semibold border-r border-slate-200 ${isZero(v) ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>
    {fmt(v)}
  </td>
);

const Row = ({ r, first, onClick, active }) => {
  const base = r.is_settlement ? "bg-amber-50" : "bg-white";
  return (
    <tr id={`row-${r.id}`} data-testid={r.is_settlement ? "table-row-settlement" : "table-row-entry"} onClick={() => onClick(r)}
      className={`${base} ${active ? "outline outline-2 -outline-offset-2 outline-red-600 mismatch-flash" : ""} cursor-pointer hover:bg-slate-50 transition-colors ${first ? "border-t-2 border-t-slate-400" : "border-t border-slate-200"}`}>
      <td className={`sticky left-0 z-10 ${base} px-3 py-2.5 border-r border-slate-300 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.12)] whitespace-nowrap ${r.is_settlement ? "border-l-4 border-l-amber-500" : ""}`}>
        <span className={`font-mono text-[13px] ${first ? "font-semibold text-slate-900" : "text-slate-400"}`} data-testid="entry-date-cell">{ddmmyyyy(r.date)}</span>
        {r.is_settlement && <span className="block text-[9px] font-bold tracking-[0.15em] text-amber-700" data-testid="settlement-badge">SETTLEMENT</span>}
      </td>
      {TABLE_COLS.map((c) =>
        c.k === "pnl" ? <PnlCell key={c.k} v={r.pnl} /> : c.k === "cashout_to" ? (
          <td key={c.k} className="px-3 py-2.5 text-[12px] font-semibold whitespace-nowrap text-orange-900 max-w-[160px] truncate border-r border-slate-200" data-testid="entry-cashout-to-cell">{r.cashout_to}</td>
        ) : c.k === "cashout_note" ? (
          <td key={c.k} className="px-3 py-2.5 text-[12px] whitespace-nowrap text-orange-900 max-w-[200px] truncate border-r border-slate-200" data-testid="entry-cashout-note-cell" title={r.cashout_note}>{r.cashout_note}</td>
        ) : (
          <td key={c.k} className={`px-3 py-2.5 text-right font-mono tabular-nums whitespace-nowrap border-r border-slate-200 ${c.strong ? "font-semibold text-slate-900" : "text-slate-700"} ${(c.k === "op_bal" && r.op_manual) || (c.k === "freeze_bal" && r.freeze_manual) ? "underline decoration-dotted decoration-blue-500 underline-offset-4" : ""}`}>
            {fmt(r[c.k])}
          </td>
        ))}
      <td className="px-3 py-2.5 font-mono text-[13px] whitespace-nowrap border-r border-slate-200 text-slate-700" data-testid="entry-diff-cell">{r.diff}</td>
      <td className="px-3 py-2.5 text-[13px] whitespace-nowrap text-slate-700 max-w-[220px] truncate border-r border-slate-200" data-testid="entry-note-cell">{r.note}</td>
      <td className="px-3 py-2.5 text-[12px] whitespace-nowrap text-red-700 max-w-[240px] truncate border-r border-slate-200" data-testid="entry-reason-cell">{r.reason}</td>
      <td className="px-3 py-2.5 text-[11px] whitespace-nowrap text-slate-500" data-testid="entry-lastchange-cell">
        {(r.updated_by_name || r.created_by_name) && <><b className="text-slate-700 font-semibold">{r.updated_by_name || r.created_by_name}</b> <span className="font-mono">{when(r.updated_at || r.created_at)}</span></>}
      </td>
    </tr>
  );
};

export const LedgerTable = ({ rows, onRowClick, activeId }) => (
  <div className="ledger-scroll overflow-auto max-h-[calc(100vh-250px)] min-h-[240px] border border-slate-300 bg-white" data-testid="ledger-table">
    <table className="w-max min-w-full text-sm border-collapse">
      <thead className="sticky top-0 z-20">
        <tr>
          <th className="sticky left-0 z-30 bg-slate-900 text-white px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-[0.12em] border-r border-slate-700">Date</th>
          {[...TABLE_COLS, { k: "diff", l: "Diff" }, { k: "note", l: "Note" }, { k: "reason", l: "Reason" }, { k: "by", l: "Last change" }].map((c) => (
            <th key={c.k} className={`px-3 py-2.5 text-[11px] font-bold uppercase tracking-[0.1em] whitespace-nowrap border-r border-slate-300 border-b-2 border-b-slate-900 ${["note", "diff", "reason", "by", "cashout_note", "cashout_to"].includes(c.k) ? "text-left" : "text-right"} ${c.h || "bg-slate-100 text-slate-700"}`}>
              {c.l}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => <Row key={r.id} r={r} active={r.id === activeId} first={i === 0 || rows[i - 1].date !== r.date} onClick={onRowClick} />)}
      </tbody>
    </table>
    {rows.length === 0 && (
      <div className="p-10 text-center text-sm text-slate-500" data-testid="ledger-empty">No entries in this range. Tap “Add entry” to start.</div>
    )}
  </div>
);
